/**
 * Hub User detail — create, edit, or invite (no list table)
 */

import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { usersAPI, groupsAPI } from '../../../utils/scheduler/api'
import { useSchedulerAuth } from '../../../contexts/SchedulerAuthContext'
import { useToast } from '../../../contexts/ToastContext'
import { canSetOtherUserPassword, GROUP_LABELS, HUB_GROUPS } from '../../../constants/hub-groups'
import ConfirmDialog from '../../scheduler/ConfirmDialog'
import { Button } from '@/components/ui/button'
import {
  DEFAULT_INVITE_MESSAGE,
  EMPTY_INVITE_DATA,
  EMPTY_USER_FORM,
  userToFormData,
} from './shared.js'
import HubUsersBreadcrumb from './HubUsersBreadcrumb.jsx'

export default function HubUserDetailPage({ mode }) {
  const navigate = useNavigate()
  const { userId } = useParams()
  const { user: currentUser } = useSchedulerAuth()
  const { success: toastSuccess, error: toastError } = useToast()
  const maySetPassword = canSetOtherUserPassword(currentUser)
  const isInvite = mode === 'invite'
  const isCreate = mode === 'create'
  const isEdit = mode === 'edit'

  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [availableGroups, setAvailableGroups] = useState([])
  const [formData, setFormData] = useState(isInvite ? EMPTY_INVITE_DATA : EMPTY_USER_FORM)
  const [deleteConfirm, setDeleteConfirm] = useState(false)

  useEffect(() => {
    const load = async () => {
      try {
        const groupsData = await groupsAPI.list()
        setAvailableGroups(groupsData.groups || [])

        if (isEdit && userId) {
          const data = await usersAPI.get(userId)
          if (data.user) {
            setFormData(userToFormData(data.user))
          } else {
            navigate('/hub/settings', { replace: true })
          }
        }
      } catch (error) {
        console.error('Error loading user:', error)
        if (isEdit) navigate('/hub/settings', { replace: true })
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [isEdit, userId, navigate])

  const selectedGroupIds = formData.group_ids || []
  const selectedGroups = availableGroups.filter((g) =>
    selectedGroupIds.includes(Number(g.id))
  )
  const isAdminGroup = selectedGroups.some((g) => g.name === HUB_GROUPS.HUB_ADMIN)

  const toggleGroup = (groupId, checked) => {
    const current = formData.group_ids || []
    const numericId = Number(groupId)
    const next = checked
      ? [...new Set([...current, numericId])]
      : current.filter((id) => id !== numericId)
    setFormData({ ...formData, group_ids: next })
  }

  const pageTitle = isInvite
    ? 'Invite User'
    : isCreate
      ? 'Add New User'
      : 'Edit User'

  const breadcrumbLabel =
    isEdit && formData.name ? formData.name : pageTitle

  const handleUserSubmit = async (e) => {
    e.preventDefault()
    if (!formData.group_ids?.length) {
      toastError('Select at least one group')
      return
    }

    try {
      setSaving(true)
      if (isEdit) {
        const updateData = {
          email: formData.email,
          name: formData.name,
          group_ids: formData.group_ids,
          active: formData.active,
          billing_enabled: formData.billing_enabled,
          notification_preference: formData.notification_preference,
          admin_email_notifications: formData.admin_email_notifications,
          phone: formData.phone || null,
          address: formData.address || null,
          city: formData.city || null,
          state: formData.state || null,
          zip_code: formData.zip_code || null,
        }
        const trimmedPassword = (formData.password || '').trim()
        if (trimmedPassword !== '') {
          if (!maySetPassword) {
            toastError('You do not have permission to reset passwords')
            return
          }
          if (trimmedPassword.length < 6) {
            toastError('Password must be at least 6 characters')
            return
          }
          if (trimmedPassword !== (formData.password_confirm || '').trim()) {
            toastError('Passwords do not match')
            return
          }
          updateData.password = trimmedPassword
        }
        await usersAPI.update(userId, updateData)
        toastSuccess('User updated.')
      } else {
        if (!maySetPassword) {
          toastError('You do not have permission to set passwords')
          return
        }
        if (!formData.password || formData.password.length < 6) {
          toastError('Password is required and must be at least 6 characters')
          return
        }
        if (formData.password !== formData.password_confirm) {
          toastError('Passwords do not match')
          return
        }
        const createData = { ...formData }
        delete createData.password_confirm
        await usersAPI.create(createData)
        toastSuccess('User created.')
      }
      navigate('/hub/settings')
    } catch (error) {
      toastError(error.message || 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  const handleInviteSubmit = async (e) => {
    e.preventDefault()
    if (!formData.name || !formData.email || !formData.group_ids?.length) {
      toastError('Name, email, and at least one group are required')
      return
    }

    try {
      setSaving(true)
      await usersAPI.invite({
        name: formData.name,
        email: formData.email,
        group_ids: formData.group_ids.map((id) => Number(id)),
        notification_preference: formData.notification_preference || '36h',
        message: formData.message || DEFAULT_INVITE_MESSAGE,
      })
      toastSuccess('Invite sent.')
      navigate('/hub/settings')
    } catch (error) {
      toastError(error.message || 'Failed to send invite')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    try {
      await usersAPI.delete(userId)
      toastSuccess('User deleted.')
      navigate('/hub/settings')
    } catch (error) {
      toastError(error.message || 'Failed to delete')
    }
  }

  if (loading) {
    return (
      <div>
        <HubUsersBreadcrumb currentLabel={pageTitle} />
        <div className="flex justify-center py-12">
          <div className="text-gray-600">Loading user...</div>
        </div>
      </div>
    )
  }

  return (
    <div>
      <HubUsersBreadcrumb currentLabel={breadcrumbLabel} />

      <ConfirmDialog
        open={deleteConfirm}
        title="Delete user?"
        message="This will permanently remove this user."
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
        onClose={() => setDeleteConfirm(false)}
        onConfirm={async () => {
          setDeleteConfirm(false)
          await handleDelete()
        }}
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-start mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">{pageTitle}</h2>
          {isInvite && (
            <p className="mt-1 text-sm text-gray-600">
              Creates an account and emails a temporary password and login link.
            </p>
          )}
        </div>
        {isEdit && (
          <Button
            type="button"
            variant="muted"
            onClick={() => setDeleteConfirm(true)}
            className="w-full border-red-200 text-red-700 hover:bg-red-50 sm:w-auto"
          >
            Delete user
          </Button>
        )}
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <form
          onSubmit={isInvite ? handleInviteSubmit : handleUserSubmit}
          className="space-y-4 max-w-2xl"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input
              type="text"
              required
              value={formData.name || ''}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="Enter user name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
            <input
              type="email"
              required
              value={formData.email || ''}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="Enter email address"
            />
          </div>

          {!isInvite && !isEdit && maySetPassword && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                <input
                  type="password"
                  required
                  value={formData.password || ''}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Min 6 characters"
                  minLength={6}
                  autoComplete="new-password"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm password *</label>
                <input
                  type="password"
                  required
                  value={formData.password_confirm || ''}
                  onChange={(e) => setFormData({ ...formData, password_confirm: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Re-enter password"
                  minLength={6}
                  autoComplete="new-password"
                />
              </div>
            </>
          )}

          {!isInvite && isEdit && maySetPassword && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New password (leave blank to keep current)
                </label>
                <input
                  type="password"
                  value={formData.password || ''}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Min 6 characters"
                  minLength={6}
                  autoComplete="new-password"
                />
              </div>
              {(formData.password || '').length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirm new password *</label>
                  <input
                    type="password"
                    value={formData.password_confirm || ''}
                    onChange={(e) => setFormData({ ...formData, password_confirm: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Re-enter new password"
                    minLength={6}
                    autoComplete="new-password"
                  />
                </div>
              )}
            </>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Groups *</label>
            {!isInvite && (
              <p className="text-xs text-gray-500 mb-2">
                Pick one primary role: <strong>{GROUP_LABELS[HUB_GROUPS.HUB_ADMIN]}</strong>,{' '}
                <strong>{GROUP_LABELS[HUB_GROUPS.VIVA_ASSOCIATE]}</strong>, or{' '}
                <strong>{GROUP_LABELS[HUB_GROUPS.RENTER]}</strong>. Add{' '}
                <strong>{GROUP_LABELS[HUB_GROUPS.CRM_ASSIGNEE]}</strong> for the Assignee dropdown,{' '}
                <strong>{GROUP_LABELS[HUB_GROUPS.CRM_NOTIFY]}</strong> for case emails, and/or{' '}
                <strong>{GROUP_LABELS[HUB_GROUPS.CRM_NUDGE]}</strong> to send team reminders in CRM.
                Omit CRM supplementary groups for operations-only admins.
              </p>
            )}
            <div className="space-y-2 border border-gray-300 rounded-md p-3 max-h-48 overflow-y-auto">
              {availableGroups.map((group) => (
                <label key={group.id} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={(formData.group_ids || []).includes(Number(group.id))}
                    onChange={(e) => toggleGroup(group.id, e.target.checked)}
                  />
                  <span className="text-sm text-gray-800">
                    {GROUP_LABELS[group.name] || group.name}
                  </span>
                </label>
              ))}
            </div>
            {(formData.group_ids || []).length === 0 && (
              <p className="text-sm text-red-500 mt-1">Select at least one group</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Room Scheduler notification preference
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Reminder emails before a room reservation — not used for CRM case alerts.
            </p>
            <select
              value={formData.notification_preference || 'none'}
              onChange={(e) =>
                setFormData({ ...formData, notification_preference: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="none">None</option>
              <option value="24h">24 hours before</option>
              <option value="36h">36 hours before</option>
              <option value="48h">48 hours before</option>
              <option value="72h">72 hours before</option>
            </select>
          </div>

          {isInvite && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Message (optional)</label>
              <textarea
                value={formData.message || ''}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={4}
                placeholder="Add a short message for the user"
                maxLength={1000}
              />
            </div>
          )}

          {!isInvite && (
            <>
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.billing_enabled !== false}
                    onChange={(e) => setFormData({ ...formData, billing_enabled: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Billing enabled</span>
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  If disabled, the user will not see Billing/Invoices and will not accrue charges.
                </p>
              </div>

              {isAdminGroup && (
                <div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.admin_email_notifications !== false}
                      onChange={(e) =>
                        setFormData({ ...formData, admin_email_notifications: e.target.checked })
                      }
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Receive Room Scheduler email notifications
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    Reservations and billing — not CRM. CRM case emails use the CRM Notification
                    group and CRM Settings.
                  </p>
                </div>
              )}

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.active !== false}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Active</span>
                </label>
              </div>

              <div className="border-t pt-4 mt-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Profile information</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={formData.phone || ''}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                    <input
                      type="text"
                      value={formData.address || ''}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                      <input
                        type="text"
                        value={formData.city || ''}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                      <input
                        type="text"
                        value={formData.state || ''}
                        onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Zip code</label>
                    <input
                      type="text"
                      value={formData.zip_code || ''}
                      onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          <div className="flex flex-wrap gap-2 pt-4 border-t">
            <Button type="submit" variant={isInvite ? 'blue' : 'success'} disabled={saving}>
              {saving
                ? 'Saving…'
                : isInvite
                  ? 'Send invite'
                  : isEdit
                    ? 'Save changes'
                    : 'Create user'}
            </Button>
            <Button asChild variant="muted">
              <Link to="/hub/settings">Cancel</Link>
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
